import { createContext, useContext, useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); return; }
    try {
      const { data } = await api.get("/favorites/ids");
      setIds(new Set(data));
    } catch {
      setIds(new Set());
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFavorite = (vendorId) => ids.has(vendorId);

  const toggle = useCallback(async (vendorId) => {
    if (!user) return { requiresLogin: true };
    const wasFav = ids.has(vendorId);
    // Optimistic update
    setIds((prev) => {
      const next = new Set(prev);
      wasFav ? next.delete(vendorId) : next.add(vendorId);
      return next;
    });
    try {
      if (wasFav) await api.delete(`/favorites/${vendorId}`);
      else await api.post("/favorites", { vendor_id: vendorId });
      return { requiresLogin: false, saved: !wasFav };
    } catch (e) {
      // Roll back on failure
      setIds((prev) => {
        const next = new Set(prev);
        wasFav ? next.add(vendorId) : next.delete(vendorId);
        return next;
      });
      throw e;
    }
  }, [ids, user]);

  return (
    <FavoritesContext.Provider value={{ ids, isFavorite, toggle, refresh }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
